import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Typography,
  Collapse,
  Button
} from '@mui/material';
import {
  PlaylistPlay,
  ExpandLess,
  ExpandMore,
  Public,
  Lock,
  Dashboard
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { listUserWatchlists } from '../../graphql/queries';
import { enrichWatchlistsWithCounts } from '../../utils/watchlistCountUtils';

const WatchlistSidebar = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [watchlists, setWatchlists] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMobile = window.matchMedia('(max-width: 900px)').matches;

  useEffect(() => {
    if (user) {
      loadWatchlists();
    }
  }, [user]);

  const loadWatchlists = async () => {
    setLoading(true);
    try {
      const client = generateClient();
      const result = await client.graphql({
        query: listUserWatchlists,
        variables: {
          filter: {
            userID: { eq: user.id }
          },
          limit: 10
        }
      });
      
      const watchlistsData = result.data?.listUserWatchlists?.items || [];
      
      // Enrich watchlists with actual item counts
      const enrichedWatchlists = await enrichWatchlistsWithCounts(watchlistsData);
      setWatchlists(enrichedWatchlists);
    } catch (error) {
      console.error('Error loading watchlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDashboardClick = () => {
    navigate('/my-watchlist');
    onClose?.();
  };

  // Show for all users, but with different content for unauthenticated

  return (
    <Box 
      sx={{ 
        width: '100%',
        background: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.3)' 
          : 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 193, 7, 0.1)'
          : '1px solid rgba(25, 118, 210, 0.1)',
        mb: 1
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ListItemButton onClick={() => setExpanded(!expanded)}>
        <ListItemIcon>
          <PlaylistPlay sx={{ color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2' }} />
        </ListItemIcon>
        <ListItemText primary="My Watchlists" />
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      
      <Collapse in={expanded || (hovered && !isMobile)} timeout="auto" unmountOnExit>
        {user ? (
          <>
            <Box sx={{ px: 2, pb: 1 }}>
              <Button
                fullWidth
                startIcon={<Dashboard />}
                onClick={handleDashboardClick}
                sx={{
                  mb: 1,
                  justifyContent: 'flex-start',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                    : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  color: 'white',
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                      : 'linear-gradient(45deg, #42a5f5, #1976d2)'
                  }
                }}
              >
                Dashboard
              </Button>
            </Box>
            
            <List component="div" disablePadding>
          {watchlists.slice(0, 5).map((watchlist) => (
            <ListItem key={watchlist.id} disablePadding>
              <ListItemButton
                sx={{ 
                  pl: 4,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? 'rgba(255, 193, 7, 0.1)'
                      : 'rgba(25, 118, 210, 0.1)'
                  }
                }}
                onClick={() => {
                  navigate(`/watchlist/${watchlist.id}`);
                  onClose?.();
                }}
              >
                <ListItemIcon>
                  {watchlist.isPublic ? <Public fontSize="small" /> : <Lock fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap>
                        {watchlist.name}
                      </Typography>
                      {watchlist.isDefault && (
                        <Chip label="Default" size="small" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={`${watchlist.itemCount || 0} items`}
                />
              </ListItemButton>
            </ListItem>
          ))}
          
              {watchlists.length > 5 && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                        +{watchlists.length - 5} more watchlists
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </>
        ) : (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sign in to create and manage your watchlists
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                navigate('/auth?mode=signin');
                onClose?.();
              }}
              sx={{
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                  : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                color: 'white'
              }}
            >
              Sign In
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default WatchlistSidebar;